import { useCallback } from "react"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { resetCreateNewPasswordFormState, resetRequestNewPasswordFormState, resetValidateCodeFormState, setCreateNewPasswordFormConfirmPasswordState, setCreateNewPasswordFormErrorsState, setCreateNewPasswordFormPasswordState, setCreateNewPasswordFormTouchedState, setPasswordRecoveryStepState, setRequestNewPasswordFormEmailState, setRequestNewPasswordFormErrorsState, setRequestNewPasswordFormTouchedState, setValidateCodeFormCodeState, setValidateCodeFormErrorsState, setValidateCodeFormTouchedState, validateCreateNewPasswordForm, validateRequestNewPasswordForm, validateValidateCodeForm } from "../../application/states/auth"
import { useAuthApi } from "../../infra/auth"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useUIService } from "../ui"

export const usePasswordRecoveryService = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const requestNewPasswordForm = useAppSelector((state) => state.auth.requestNewPasswordForm)
  const validateCodeForm = useAppSelector((state) => state.auth.validateCodeForm)
  const createNewPasswordForm = useAppSelector((state) => state.auth.createNewPasswordForm)

  const currentStep = useAppSelector((state) => state.auth.currentPasswordRecoveryStep)

  const {
    requeNewPasswordApi,
    validateCodeApi,
    createNewPasswordApi
  } = useAuthApi()

  const {
    setAlert,
    setLoading
  } = useUIService()

  const prevStep = useCallback(() => {
    dispatch(setPasswordRecoveryStepState({
      step: currentStep > 0 ? currentStep - 1 : 0
    }))
    if (currentStep === 1) {
      navigate('/auth/password-recovery/request-new-password')
    } else if (currentStep === 2) {
      navigate('/auth/password-recovery/validate-code')
    }
  }, [currentStep, dispatch, navigate])

  const setRequestNewPasswordFormEmail = useCallback((data: {
    email: string
  }) => {
    dispatch(setRequestNewPasswordFormEmailState({
      email: data.email
    }))
  }, [dispatch])

  const requestNewPassword = useCallback(async () => {
    const errors = validateRequestNewPasswordForm(requestNewPasswordForm.fields)
    if (errors.length > 0) {
      dispatch(setRequestNewPasswordFormErrorsState({
        errors: errors
      }))
      dispatch(setRequestNewPasswordFormTouchedState({
        touched: true
      }))
    } else {
      const loadingId = Math.random().toString()
      try {
        setLoading({
          loading: true,
          message: 'Solicitando nova senha',
          id: loadingId
        })
        const result = await requeNewPasswordApi({
          email: requestNewPasswordForm.fields.email
        })
        dispatch(setPasswordRecoveryStepState({
          step: 1
        }))
        setAlert({
          message: result?.message || 'Código de recuperação enviado para o seu e-mail',
          type: 'success'
        })
        dispatch(setRequestNewPasswordFormErrorsState({
          errors: []
        }))
        dispatch(setRequestNewPasswordFormTouchedState({
          touched: false
        }))
        navigate('/auth/password-recovery/validate-code')
      } catch (error: any) {
        setAlert({
          message: error?.message ?? 'Erro ao solicitar nova senha',
          type: 'error'
        })
      } finally {
        setLoading({
          loading: false,
          id: loadingId
        })
      }
    }
  }, [dispatch, navigate, requeNewPasswordApi, requestNewPasswordForm.fields, setAlert, setLoading])

  const setValidateCodeFormCode = useCallback((data: {
    code: string
  }) => {
    dispatch(setValidateCodeFormCodeState({
      code: data.code
    }))
  }, [dispatch])

  const validateCode = useCallback(async () => {
    const errors = validateValidateCodeForm(validateCodeForm.fields)
    if (errors.length > 0) {
      dispatch(setValidateCodeFormErrorsState({
        errors: errors
      }))
      dispatch(setValidateCodeFormTouchedState({
        touched: true
      }))
    } else {
      const loadingId = Math.random().toString()
      try {
        setLoading({
          loading: true,
          message: 'Validando código de recuperação',
          id: loadingId
        })
        const isValid = await validateCodeApi({
          email: requestNewPasswordForm.fields.email,
          code: validateCodeForm.fields.code
        })
        
        if (!isValid) {
          throw new Error('Código de verificação inválido ou expirado')
        }
        dispatch(setPasswordRecoveryStepState({
          step: 2
        }))
        setAlert({
          message: 'Código de recuperação validado com sucesso',
          type: 'success'
        })
        dispatch(setValidateCodeFormErrorsState({
          errors: []
        }))
        dispatch(setValidateCodeFormTouchedState({
          touched: false
        }))
        navigate('/auth/password-recovery/create-new-password')
      } catch (error: any) {
        setAlert({
          message: error?.message ?? 'Erro ao validar código de recuperação',
          type: 'error'
        })
      } finally {
        setLoading({
          loading: false,
          id: loadingId
        })
      }
    }
  }, [dispatch, navigate, requestNewPasswordForm.fields.email, setAlert, setLoading, validateCodeApi, validateCodeForm.fields])

  // Create New Password Actions

  const setCreateNewPasswordFormPassword = useCallback((data: {
    password: string
  }) => {
    dispatch(setCreateNewPasswordFormPasswordState({
      password: data.password
    }))
  }, [dispatch])

  const setCreateNewPasswordFormConfirmPassword = useCallback((data: {
    confirmPassword: string
  }) => {
    dispatch(setCreateNewPasswordFormConfirmPasswordState({
      confirmPassword: data.confirmPassword
    }))
  }, [dispatch])

  const createNewPassword = useCallback(async () => {
    const errors = validateCreateNewPasswordForm({
      password: createNewPasswordForm.fields.password,
      confirmPassword: createNewPasswordForm.fields.confirmPassword
    })
    if (errors.length > 0) {
      dispatch(setCreateNewPasswordFormErrorsState({
        errors: errors
      }))
      dispatch(setCreateNewPasswordFormTouchedState({
        touched: true
      }))
    } else {
      const loadingId = Math.random().toString()
      try {
        setLoading({
          loading: true,
          message: 'Criando nova senha',
          id: loadingId
        })
        const code = searchParams.get('code') // HashUrl do link do email
        if (code) {
          // Se houver código na URL (do link do email), usar fluxo com código
          await createNewPasswordApi({
            code: code,
            password: createNewPasswordForm.fields.password
          })
        } else {
          // Fluxo com código de verificação de 6 dígitos
          await createNewPasswordApi({
            email: requestNewPasswordForm.fields.email,
            codigo: validateCodeForm.fields.code,
            password: createNewPasswordForm.fields.password
          })
        }
        setAlert({
          message: 'Nova senha criada com sucesso',
          type: 'success'
        })
        dispatch(setCreateNewPasswordFormErrorsState({
          errors: []
        }))
        dispatch(setCreateNewPasswordFormTouchedState({
          touched: false
        }))

      } catch (error: any) {
        setAlert({
          message: error?.message ?? 'Erro ao criar nova senha',
          type: 'error'
        })
      } finally {
        setLoading({
          loading: false,
          id: loadingId
        })
        dispatch(resetRequestNewPasswordFormState())
        dispatch(resetValidateCodeFormState())
        dispatch(resetCreateNewPasswordFormState())
        dispatch(setPasswordRecoveryStepState({
          step: 0
        }))
        navigate('/auth/login')
        
      }
    }
  }, [createNewPasswordApi, createNewPasswordForm.fields.confirmPassword, createNewPasswordForm.fields.password, dispatch, navigate, requestNewPasswordForm.fields.email, setAlert, setLoading, validateCodeForm.fields.code, searchParams])

  return {
    setRequestNewPasswordFormEmail,
    requestNewPassword,
    prevStep,
    // Validate Code Actions
    setValidateCodeFormCode,
    validateCode,
    // Create New Password Actions
    setCreateNewPasswordFormPassword,
    setCreateNewPasswordFormConfirmPassword,
    createNewPassword
  }
}